import { gql, useQuery, WatchQueryFetchPolicy } from "@apollo/client";
import { getFetchPolicyOptions } from "api/utils";
import { PortfolioAnalyticsQuery } from "./types";

const PORTFOLIO_ANALYTICS = gql`
  query AnalyticsReturn(
    $ids: [Long]
    $startDate: String
    $endDate: String
    $dataKey: String
  ) {
    analytics(
      parameters: {
        pfIds: $ids
        includeOnlyCashAccounts: false
        startDate: $startDate
        endDate: $endDate
        paramsSet: [
          {
            key: $dataKey
            timePeriodCodes: "GIVEN"
            grouppedByProperties: [SECURITY]
            includeData: false
            includeChildren: true
            drilldownEnabled: false
            limit: 0
            locale: "en-US"
            customColumnDefinitions: [
              "paidCapitalCustom"
              "paidCapitalChange"
              "unrealizedProfitsPerc"
              "unrealizedProfitsYearToDate"
              "unrealizedProfitsYearToDatePerc"
              "realizedProfitsIncOther"
            ]
          }
        ]
      }
    ) {
      total: grouppedAnalytics(key: $dataKey) {
        code
        name
        figures: firstAnalysis {
          startDate
          amount
          marketValue
          marketValueBase
          totalProfitsInclAccruedInterest
          totalNetProfitsInclAccruedInterest
          realizedProfits
          customColumnValues
          twr
        }
        positions: grouppedAnalytics {
          code
          name
          security {
            id
            isinCode
            securitySubTypeName
            securitySubTypeCode
            linkedSecurity {
              id
            }
            keyFigureValues
          }
          figures: firstAnalysis {
            amount
            customColumnValues
            purchaseValue
            accruedInterest
            marketValue
            marketUnitPrice
            marketUnitPriceDate
            marketFxRate
            shareOfTotal
            fxRate
            realizedProfits
            otherProfits
            twr
            totalNetProfitsInclAccruedInterest
          }
        }
      }
    }
  }
`;

interface UseGetPortfolioAnalyticsOptions {
  startDate?: string;
  endDate?: string;
  fetchPolicy?: WatchQueryFetchPolicy;
  onCompleted?: (data: unknown) => void;
}

export const useGetPortfolioAnalytics = (
  ids: number | undefined,
  dataKey = "cacheKeyForPositionsAndFigures",
  options: UseGetPortfolioAnalyticsOptions = {}
) => {
  const { startDate, endDate, fetchPolicy, onCompleted } = options;

  const defaultPolicy = getFetchPolicyOptions("useGetPortfolioAnalytics");

  const { data, loading, error } = useQuery<PortfolioAnalyticsQuery>(
    PORTFOLIO_ANALYTICS,
    {
      skip: !ids || !dataKey,
      variables: { ids, startDate, endDate, dataKey },
      fetchPolicy: fetchPolicy ?? defaultPolicy.fetchPolicy,
      nextFetchPolicy: "cache-first",
      notifyOnNetworkStatusChange: false,
      onCompleted: onCompleted ?? defaultPolicy.onCompleted,
    }
  );

  return { data, loading, error };
};